/**
 * Gapless linear16 PCM playback via Web Audio — the real PcmSink for the
 * Aura speak-stream client. Frames are converted to AudioBuffers and
 * scheduled back to back on the context clock, so consecutive WS frames
 * play as one continuous utterance. clear() (barge-in) stops every
 * scheduled source immediately.
 *
 * Browser-only by nature; kept deliberately thin. The protocol/facade logic
 * lives in aura-stream-speech.ts where jsdom tests cover it with fakes.
 */
import { SPEAK_SAMPLE_RATE, type PcmSink } from './aura-stream-speech'

export function createPcmPlayer(ctx?: AudioContext): PcmSink & { context: AudioContext } {
  const context = ctx ?? new AudioContext({ sampleRate: SPEAK_SAMPLE_RATE })
  let playhead = 0
  let sources: AudioBufferSourceNode[] = []

  return {
    context,
    write(frame: ArrayBuffer): void {
      const pcm = new Int16Array(frame)
      if (pcm.length === 0) return
      const buffer = context.createBuffer(1, pcm.length, SPEAK_SAMPLE_RATE)
      const channel = buffer.getChannelData(0)
      for (let i = 0; i < pcm.length; i++) channel[i] = pcm[i]! / 32768
      const source = context.createBufferSource()
      source.buffer = buffer
      source.connect(context.destination)
      const startAt = Math.max(context.currentTime, playhead)
      source.start(startAt)
      playhead = startAt + buffer.duration
      sources.push(source)
      source.onended = () => {
        sources = sources.filter((s) => s !== source)
      }
    },
    clear(): void {
      for (const s of sources.splice(0)) {
        try {
          s.stop()
        } catch {
          /* already ended */
        }
      }
      playhead = 0
    },
    drain(): Promise<void> {
      const remaining = Math.max(0, playhead - context.currentTime)
      return new Promise((resolve) => setTimeout(resolve, remaining * 1000))
    },
  }
}
